import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useQuery } from '@/api/query'
import { SpendPanel } from '@/components/SpendPanel'
import { useTenants } from '@/tenant/context'
import { Empty, ErrorState, Loading } from '@/ui/states'
import { DeliverabilityPanel } from './DeliverabilityPanel'
import { Verdict } from './verdict'

type Funnel = Schema<'FunnelResponse'>
type Stage = Schema<'FunnelStageOut'>
type Channel = Schema<'FunnelChannelOut'>

/** The windows `app/outreach/funnel.WINDOWS` offers, and the only ones it accepts. */
const WINDOWS = [7, 30, 90] as const
type Window = (typeof WINDOWS)[number]

/**
 * The outreach dashboard's headline: S-D4's funnel, on real data (S-B31a).
 *
 * **Why the funnel is the whole top of the screen** is the design's own note,
 * and it is worth keeping: *"outreach only has one question — is anything coming
 * out the other end"*. Deliverability, ban risk and spend sit beside it in
 * S-B31b and S-B31c; this file owns the funnel and the per-channel split.
 *
 * **Every number comes from the endpoint, including the captions and the bar
 * widths.** Nothing is recomputed here — not a percentage, not a share. The
 * server decides what a stage is a share of (`replied` is quoted against *sent*
 * rather than against the stage drawn before it, and the module says why), so a
 * change to that rule lands on the screen without a second implementation of it
 * drifting in TypeScript.
 *
 * **A caption that is absent is absent, not zero.** `note` is `null` when the
 * stage it would be a share of is empty, and this renders nothing rather than
 * `0.0%` — a tenant who has sent nothing does not have a 0% open rate.
 *
 * **The empty state asks the right question.** `empty` and `ever_active` are two
 * different facts: a quiet month wants a wider window, a brand-new organisation
 * wants a connected channel, and offering the first to somebody in the second
 * state is advice that cannot work.
 */
export function DashboardPage(): JSX.Element {
  const [days, setDays] = useState<Window>(30)

  const funnel = useQuery(
    (signal) =>
      call(() => api.GET('/api/v1/outreach/funnel', { params: { query: { days } }, signal })),
    [days],
  )

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row" style={{ justifyContent: 'space-between' }}>
        <h1 className="ar-h1">Outreach</h1>
        {/* Parity P-ODASH-01 (2026-09-13). `outreach.html:122-125` draws one
            ghost *Last 30 days* button beside *New campaign*; the three
            windows are behind it as a menu, so the choice is kept and the bar
            is drawn as designed. */}
        <div className="ar-row app-outreach__actions">
          <PeriodMenu days={days} onChange={setDays} />
          {/* OUTR-1. The screen's one action, and it was missing entirely: every
              other route into composing a sequence is from a lead. `#!compose`
              in the mockup is `/outreach/compose` here. */}
          <Link className="ar-btn ar-btn--primary ar-btn--sm" to="/outreach/compose">
            New campaign
          </Link>
        </div>
      </div>

      {funnel.state.status === 'loading' ? <Loading label="Loading your funnel" lines={4} /> : null}

      {funnel.state.status === 'error' ? (
        <div className="ar-card">
          <ErrorState
            failure={funnel.state.failure}
            title="Could not load your funnel"
            onRetry={funnel.refetch}
          />
        </div>
      ) : null}

      {funnel.state.status === 'ready' ? <Ready data={funnel.state.data} /> : null}

      {/* Parity P-ODASH-02: `outreach.html:151` puts By channel and
          Deliverability side by side under the funnel. `ar-grid--2`, which the
          mockup uses, collapses to one column below two 18rem tracks. The
          deliverability card stands whatever the funnel is doing. */}
      <div className="ar-grid ar-grid--2">
        {funnel.state.status === 'ready' && !funnel.state.data.empty ? (
          <ByChannel data={funnel.state.data} />
        ) : null}
        <DeliverabilityPanel />
      </div>

      {/* **Three panels, three queries, three failure modes.** The funnel is an
          aggregate over this tenant's own tables, deliverability asks somebody
          else's nameservers, and spend reads the ledger in `public` — so one of
          them being unavailable must not blank the others. Each owns its
          loading, empty and error states (S-B31b, S-B31c, C1). Account health
          and the send window are `/outreach/health` now (parity P-HLTH-01),
          the screen `outreach.html#health` draws.

          **Spend is last on purpose.** S-D4's own note says outreach has one
          question — *is anything coming out the other end* — so the funnel is
          the top of the screen and what it cost is the footnote to it, not the
          headline. */}
      <SpendPanel module="outreach" />
    </div>
  )
}

/**
 * The period control as `outreach.html:123` draws it — one ghost button
 * reading the current window — with the other windows in a menu behind it.
 * Closes on Escape and on a click anywhere else, like `TopBar`'s account menu.
 */
function PeriodMenu({
  days,
  onChange,
}: {
  days: Window
  onChange: (days: Window) => void
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const wrapper = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDocument(event: MouseEvent | KeyboardEvent): void {
      if (event instanceof KeyboardEvent) {
        if (event.key === 'Escape') setOpen(false)
        return
      }
      if (event.target instanceof Node && !wrapper.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocument)
    document.addEventListener('keydown', onDocument)
    return () => {
      document.removeEventListener('mousedown', onDocument)
      document.removeEventListener('keydown', onDocument)
    }
  }, [open])

  return (
    <div className="app-menu" ref={wrapper}>
      <button
        type="button"
        className="ar-btn ar-btn--ghost ar-btn--sm"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Period: last ${String(days)} days`}
        onClick={() => {
          setOpen((current) => !current)
        }}
      >
        Last {days} days
      </button>
      {open ? (
        <div className="app-menu__panel" role="menu" aria-label="Period">
          <div className="app-menu__items">
            {WINDOWS.map((window) => (
              <button
                key={window}
                type="button"
                role="menuitemradio"
                aria-checked={days === window}
                onClick={() => {
                  onChange(window)
                  setOpen(false)
                }}
              >
                Last {window} days
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function Ready({ data }: { data: Funnel }): JSX.Element {
  if (data.empty) return <Nothing data={data} />
  return (
    <div className="app-funnel">
      {data.stages.map((stage) => (
        <StageCard key={stage.key} stage={stage} />
      ))}
    </div>
  )
}

/**
 * The empty state, in its three forms.
 *
 * A real state rather than six zeroes, which is S-D1's rule for one — *never a
 * bare "no data": it says what would be here and how to get it* — and this
 * session's exit criterion.
 *
 * **The third form is the one that was missing (2026-09-12).** `ever_active`
 * says whether anything has ever come through the funnel; it says nothing about
 * whether a channel is connected. A tenant who had linked LinkedIn — the panel
 * under this one was already reporting the account healthy — was still being
 * told *Connect a channel* and sent to the Connections screen to do the thing
 * they had just done. A held connection with nothing through it yet is a
 * different state with a different next step: a search, which is what fills
 * the first stage. Until 2026-09-12 nothing in the product could start one —
 * `POST /outreach/discovery/runs` had no caller — so the link goes to the
 * keywords page, where *Search now* lives beside the allowance badge.
 */
function Nothing({ data }: { data: Funnel }): JSX.Element {
  // `useTenants` rather than `useActiveTenant`, for `SendWindowPanel`'s
  // reason: this renders inside a dashboard whose other panels may still be
  // loading, and a throw here takes them all down. No active tenant reads as
  // entitled, which is the permissive direction `TenantOut.access` argues for.
  const { active } = useTenants()
  const entitled = active === null || active.tenant.automation_available
  // `linked` rather than `connected`: LinkedIn is never `connected` (it cannot
  // carry a message) and it is the channel that fills the top of the funnel.
  const held = data.channels.filter((channel) => channel.linked || channel.connected)
  return (
    <div className="ar-card">
      {data.ever_active ? (
        <Empty
          icon="✉"
          title={`Nothing in the last ${String(data.days)} days`}
          body="There is outreach history on this organisation, just not in this period. Try a wider window, or start a discovery run to find new prospects."
          action={
            <Link className="ar-btn ar-btn--ghost" to="/settings/connections">
              Open connections
            </Link>
          }
        />
      ) : held.length > 0 && !entitled ? (
        <Empty
          /* 2026-09-12. The fourth form: connected, keywords in, browser
             paired — and every search the browser asks for is refused because
             a trial does not get unattended work (`app/billing/trial.py`).
             Until this the screen said *press Search now*, the button queued
             twelve searches, and the only place the refusal showed was the
             extension's popup. */
          icon="⌕"
          title="Connected — searches start when you choose a plan"
          body={`${listOf(held.map((channel) => channel.label))} ${held.length === 1 ? 'is' : 'are'} connected. LinkedIn searches run in your paired browser while nobody is watching, and that waits for a subscription. Everything you set up now — keywords, packs, the send window — runs the moment you subscribe.`}
          action={
            <Link className="ar-btn ar-btn--primary" to="/billing/plans">
              See the plans
            </Link>
          }
        />
      ) : held.length > 0 ? (
        <Empty
          icon="⌕"
          title="Connected, and nothing searched yet"
          body={`${listOf(held.map((channel) => channel.label))} ${held.length === 1 ? 'is' : 'are'} connected. Leads arrive once your paired browser runs a search: add the keywords it should look for, then press Search now — posts that match become leads, leads become sends, and replies land in your inbox.`}
          action={
            <Link className="ar-btn ar-btn--primary" to="/outreach/packs">
              Start a search
            </Link>
          }
        />
      ) : (
        <Empty
          icon="✉"
          title="Nothing has gone out yet"
          body="Connect LinkedIn and a sender address, and lead discovery fills this funnel: posts found become leads, leads become sends, and replies land in your inbox."
          action={
            <Link className="ar-btn ar-btn--primary" to="/settings/connections">
              Connect a channel
            </Link>
          }
        />
      )}
    </div>
  )
}

/** `LinkedIn`, `LinkedIn and Email`, `LinkedIn, Email and WhatsApp`. */
function listOf(labels: string[]): string {
  if (labels.length <= 1) return labels.join('')
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1] ?? ''}`
}

/** One of the six stages: its number, its bar, and the line under it. */
function StageCard({ stage }: { stage: Stage }): JSX.Element {
  return (
    <div className="app-stage">
      <div className="app-stage__label">{stage.label}</div>
      <div className="app-stage__n">{stage.value.toLocaleString()}</div>
      <div
        className="app-bar"
        role="meter"
        aria-label={`${stage.label}, ${String(stage.share_of_first)}% of the first stage`}
        aria-valuenow={stage.share_of_first}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <i className="app-bar__fill" style={{ width: `${String(stage.share_of_first)}%` }} />
      </div>
      {/* Rendered as an empty element rather than omitted, so the six cards keep
          one height when a stage has no rate to state. */}
      <div className="app-stage__note">{stage.note ?? ''}</div>
    </div>
  )
}

/**
 * The per-channel table.
 *
 * **Every channel in the catalogue has a row, including the ones nobody has
 * connected**, because an absent row and a zero row are the same pixel and only
 * one of them has something to do about it.
 */
function ByChannel({ data }: { data: Funnel }): JSX.Element {
  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h2>By channel</h2>
      </div>
      <div className="ar-table-wrap" tabIndex={0}>
        <table className="ar-table">
          <thead>
            <tr>
              <th>Channel</th>
              <th className="ar-num">Sent</th>
              <th className="ar-num">Opened</th>
              <th className="ar-num">Replied</th>
              <th className="ar-num">Rate</th>
              {/* OUTR-3: *Health*, not *Status*. The column the mockup draws
                  answers whether anything good is coming out of the channel; the
                  one that was here answered only whether the pipe was open. */}
              <th>Health</th>
            </tr>
          </thead>
          <tbody>
            {/* The mockup's row order (P-ODASH-03): Email, LinkedIn, WhatsApp.
                A channel this file does not know sorts last. */}
            {[...data.channels]
              .sort((a, b) => rank(a.channel) - rank(b.channel))
              .map((channel) => (
                <ChannelRow key={channel.channel} channel={channel} />
              ))}
          </tbody>
        </table>
        {/* The mockup's footnote (P-ODASH-04) was the spec's own §5 paragraph,
            word for word — section numbers, the owner's decision date, AugStash's
            product history. Written for the people building the product, and a
            tenant read it on 2026-09-19. This says the same two facts in the
            tenant's terms: which channels carry a message, and why LinkedIn has a
            row that never will. */}
        <p className="ar-subtle app-funnel__caption">
          Outreach goes out on <strong>email and WhatsApp</strong>. LinkedIn is listed because it is
          connected for finding leads and publishing posts, not for messaging — LinkedIn offers no
          way for a service like this one to send or read messages on your behalf, so that row never
          carries a send or a reply.
        </p>
      </div>
    </section>
  )
}

/** `outreach.html:157-162`'s order: Email, LinkedIn, WhatsApp. */
const ORDER = ['email', 'linkedin', 'whatsapp']
function rank(channel: string): number {
  const at = ORDER.indexOf(channel)
  return at === -1 ? ORDER.length : at
}

function ChannelRow({ channel }: { channel: Channel }): JSX.Element {
  return (
    <tr>
      <td>
        {/* The LinkedIn chip leads to the account-health screen — the way to
            `/outreach/health` from the dashboard that moved it there. */}
        {channel.channel === 'linkedin' ? (
          <Link className="app-chan app-chan--linkedin" to="/outreach/health">
            {channel.label}
          </Link>
        ) : (
          <span className={`app-chan app-chan--${channel.channel}`}>{channel.label}</span>
        )}
      </td>
      <td className="ar-num">{channel.sent.toLocaleString()}</td>
      <td className="ar-num">{channel.opened.toLocaleString()}</td>
      {/* An em dash rather than 0 where a reply can never arrive: zero is a
          measurement, and this is not one. */}
      <td className="ar-num">{channel.receivable ? channel.replied.toLocaleString() : '—'}</td>
      <td className="ar-num">
        {channel.reply_rate === null ? '—' : `${channel.reply_rate.toFixed(1)}%`}
      </td>
      <td>
        <Health channel={channel} />
      </td>
    </tr>
  )
}

/**
 * This channel's verdict — OUTR-3 and OUTR-41.
 *
 * **The words and the tone both arrive from the server** (`funnel.channel_health`),
 * which is the rule the rest of this file already follows and the reason the
 * component that used to be here is gone: it re-decided *No messaging API /
 * Not connected / Nothing sent / Sending* from three fields, so the meaning of a
 * row lived in TypeScript where nothing else on this screen's meaning does. The
 * threshold that separates *Good* from *Low volume* is a rule of the same kind as
 * "what is `replied` a share of", and it belongs in the same module.
 *
 * **`reason` stays here.** It is the sentence explaining an unconnected channel
 * and it is genuinely a tooltip — the verdict is the answer, and *why RCS is not
 * available on this deployment* is the follow-up question, not the column.
 */
function Health({ channel }: { channel: Channel }): JSX.Element {
  return (
    <span title={channel.connected ? undefined : (channel.reason ?? undefined)}>
      <Verdict tone={channel.health.tone}>{channel.health.text}</Verdict>
    </span>
  )
}
